// 🩺 B252 (AI_114) — Sub-aba "Documentos de Enfermagem" (exclusiva da enfermagem)
// ─────────────────────────────────────────────────────────────
// Lista os documentos próprios do serviço de enfermagem do paciente e permite
// criar/editar/imprimir/excluir. Hoje: Declaração de Agendamento (extensível a
// outros documentos no futuro). Renderizada como sub-aba na AbaPrescricoes só
// para enfermeiro/admin (gate ehEnfermeiro).

import React, { useEffect, useState } from "react";
import { FileText, Plus, Printer, Pencil, Trash2, Clock } from "lucide-react";
import { Paciente } from "../../../types";
import { theme } from "../../../shared/styles/theme";
import { useAuth } from "../../auth/context/AuthContext";
import { DeclaracaoAgendamento } from "./DeclaracaoAgendamento";
import {
  gerarPDFDeclaracaoAgendamento,
  montarConfigDeclaracao,
} from "../services/gerarDeclaracaoAgendamento";
import {
  assinarDocumentosEnfermagem,
  excluirDocumentoEnfermagem,
  DocumentoEnfermagem,
} from "../services/documentosEnfermagem";

interface Props {
  paciente: Paciente;
}

type ModalState = { aberto: false } | { aberto: true; documento: DocumentoEnfermagem | null };

export default function DocumentosEnfermagem({ paciente }: Props) {
  const { usuarioLogado } = useAuth();
  const [docs, setDocs] = useState<DocumentoEnfermagem[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [modal, setModal] = useState<ModalState>({ aberto: false });

  useEffect(() => {
    setCarregando(true);
    const unsub = assinarDocumentosEnfermagem(paciente.id, (lista) => {
      setDocs(lista);
      setCarregando(false);
    });
    return () => unsub();
  }, [paciente.id]);

  const excluir = async (doc: DocumentoEnfermagem) => {
    if (!usuarioLogado) return;
    if (!window.confirm(`Excluir "${doc.titulo}"? Esta ação fica registrada na trilha de auditoria.`))
      return;
    try {
      await excluirDocumentoEnfermagem({
        id: doc.id,
        pacienteId: paciente.id,
        rotulo: doc.titulo,
        autor: { id: usuarioLogado.id || "", nome: usuarioLogado.nome || "" },
      });
    } catch (e) {
      console.error("Erro ao excluir documento de enfermagem:", e);
      alert("Não foi possível excluir. Tente novamente.");
    }
  };

  const formatarCriadoEm = (doc: DocumentoEnfermagem): string => {
    try {
      const dt = doc.criadoEm?.toDate?.();
      return dt ? dt.toLocaleDateString("pt-BR") : "";
    } catch {
      return "";
    }
  };

  return (
    <div style={{ fontFamily: theme.fonts.sans }}>
      {/* Ação: nova declaração */}
      <div style={{ marginBottom: "14px" }}>
        <button
          onClick={() => setModal({ aberto: true, documento: null })}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "10px 18px",
            backgroundColor: theme.primary[600],
            color: "white",
            border: "none",
            borderRadius: "10px",
            fontSize: "14px",
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          <Plus size={16} /> Nova Declaração de Agendamento
        </button>
      </div>

      {/* Lista */}
      {carregando ? (
        <div style={{ textAlign: "center", padding: "32px", color: theme.gray[500] }}>
          <Clock size={28} style={{ marginBottom: "8px" }} />
          <p>Carregando documentos...</p>
        </div>
      ) : docs.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            padding: "32px",
            color: theme.gray[500],
            fontSize: "14px",
          }}
        >
          <FileText size={40} color={theme.gray[300]} style={{ marginBottom: "10px" }} />
          <p>Nenhum documento de enfermagem para este paciente ainda.</p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {docs.map((doc) => (
            <div
              key={doc.id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                padding: "12px 14px",
                backgroundColor: "white",
                border: "1px solid " + theme.gray[200],
                borderRadius: "10px",
              }}
            >
              <div
                style={{
                  width: "36px",
                  height: "36px",
                  borderRadius: "8px",
                  backgroundColor: "#fff7ed",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <FileText size={18} color="#ea580c" />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: "14px", fontWeight: 600, color: theme.gray[800] }}>
                  Declaração de Agendamento
                </div>
                <div style={{ fontSize: "12px", color: theme.gray[500] }}>
                  {doc.dados?.agendamento}
                  {doc.dados?.data ? ` • ${doc.dados.data}` : ""}
                  {doc.criadoPorNome ? ` • ${doc.criadoPorNome}` : ""}
                  {formatarCriadoEm(doc) ? ` • ${formatarCriadoEm(doc)}` : ""}
                </div>
              </div>
              <div style={{ display: "flex", gap: "6px", flexShrink: 0 }}>
                <BotaoIcone
                  title="Imprimir / PDF"
                  onClick={() =>
                    gerarPDFDeclaracaoAgendamento(
                      paciente,
                      doc.dados,
                      montarConfigDeclaracao(
                        doc.criadoPorNome || "",
                        doc.criadoPorRegistro || ""
                      )
                    )
                  }
                >
                  <Printer size={16} />
                </BotaoIcone>
                <BotaoIcone
                  title="Editar"
                  onClick={() => setModal({ aberto: true, documento: doc })}
                >
                  <Pencil size={16} />
                </BotaoIcone>
                <BotaoIcone title="Excluir" onClick={() => excluir(doc)} perigo>
                  <Trash2 size={16} />
                </BotaoIcone>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal.aberto && (
        <DeclaracaoAgendamento
          paciente={paciente}
          documento={modal.documento}
          onClose={() => setModal({ aberto: false })}
        />
      )}
    </div>
  );
}

function BotaoIcone({
  children,
  onClick,
  title,
  perigo,
}: {
  children: React.ReactNode;
  onClick: () => void;
  title: string;
  perigo?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      title={title}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "32px",
        height: "32px",
        backgroundColor: "white",
        border: "1px solid " + theme.gray[300],
        borderRadius: "8px",
        cursor: "pointer",
        color: perigo ? "#dc2626" : theme.gray[700],
      }}
    >
      {children}
    </button>
  );
}
